import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/erp/integrations/branches/mappings';
export const getErpBranchMappingsEndpointIdentifier = endpoint;

export interface IErpBranchMappingData {
  id: number;
  type: string;
  name: string;
  erpBranchId: string | null;
}

type TData = {
  pageCount: number;
  total: number;
  data: IErpBranchMappingData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    restaurantId?: number;
    supplierId?: number;
    supplierType?: string;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  restaurantId?: number;
  supplierId?: number;
  supplierType?: string;
};
type ReturnType = UseQueryResult<TData, TError>;

export function useGetErpBranchMappingsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort } = getPageParams();
  const { restaurantId, supplierId, supplierType, ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: {
          page,
          limit,
          sort,
          ...(restaurantId && { restaurantId }),
          ...(supplierId && { supplierId }),
          ...(supplierType && { supplierType }),
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        restaurantId,
        supplierId,
        supplierType,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
